import axios, { AxiosError, AxiosInstance } from 'axios';
import { appService, authenticationService, authorizationManagerService } from '..';

export default class AxiosService {
    public instance: AxiosInstance = axios.create({
        baseURL: process.env.VUE_APP_API_URL,
        headers: { 'Cache-Control': 'no-cache' }
    });

    public anonymousInstance: AxiosInstance = axios.create({
        baseURL: process.env.VUE_APP_API_URL,
        headers: { 'Cache-Control': 'no-cache' }
    });

    private handlingUnauthorized = false;

    constructor() {
        this.instance.interceptors.request.use(
            async config => {
                const application = appService.getCurrentApplication();
                const token = await authenticationService.accessToken;

                config.params = { ...{ client_id: application.clientId }, ...config.params };

                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }

                return config;
            },
            error => Promise.reject(error)
        );

        this.instance.interceptors.response.use(
            response => response,

            async (error: AxiosError) => {
                const status = error && error.response ? error.response.status : undefined;

                if ((status === 401 || status === 403) && !this.handlingUnauthorized) {
                    this.handlingUnauthorized = true;

                    try {
                        // Try silent renew before clearing the session
                        await authenticationService.userManager.signinSilent();
                        await authorizationManagerService.refreshPermissions();
                        // Session recovered, reload to retry with the fresh token
                        window.location.reload();
                    } catch {
                        // Silent renew failed: clean up. No redirect — the router guard will
                        // handle re-authentication via signinSilent() on the next navigation.
                        authorizationManagerService.resetPermissions();
                        await authenticationService.userManager.removeUser();
                    } finally {
                        this.handlingUnauthorized = false;
                    }
                }

                return Promise.reject(error);
            }
        );

        this.anonymousInstance.interceptors.request.use(
            config => {
                const application = appService.getCurrentApplication();

                config.params = { ...{ client_id: application.clientId }, ...config.params };

                return config;
            },
            error => Promise.reject(error)
        );
    }
}

export const axiosService = new AxiosService();
